import { MenuItem } from "@/components/Menu";
import { Link, Path, Params } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import { useMemo, useState, useEffect, useRef } from "react";
import { LitMenu, LitMenuItem } from "@/components/Menu/LitMenu";
import Divider from "@/components/Divider";
import Icon from "@/components/Icon";
import classNames from "classnames";
import type { MaterialNames } from "@/components/material_symbol_names";
import KeyboardShortcutHint from "@/components/KeyboardShortcutHint";
import "./NavItem.css";

type NavItemGoNavProps = {
  goNav?: {
    showChordKeyCaps?: boolean;
    chordKey?: string;
    dataTestId?: string;
    modeActive?: boolean;
  };
};

type NavItemMeasureCloneProp = {
  /**
   * Paint the label via CSS `content` instead of a text node.
   * Used for offscreen overflow measure clones so `getByText` / find-in-page
   * do not see duplicate destination labels beside the live header and page chrome.
   */
  measureClone?: boolean;
};

type NavItemLinkProps = {
  label: string;
  link: LinkProps<Path, Params>;
  onClick?: never;
  active?: boolean;
  menuItems?: never;
  menuYOffset?: never;
  iconName?: MaterialNames;
  hideIcon?: boolean;
  orientation?: "vertical" | "horizontal";
} & NavItemMeasureCloneProp &
  NavItemGoNavProps;

type NavItemClickProps = {
  label: string;
  link?: never;
  onClick: () => void;
  active?: boolean;
  menuItems?: never;
  menuYOffset?: never;
  iconName?: MaterialNames;
  hideIcon?: boolean;
  orientation?: "vertical" | "horizontal";
} & NavItemMeasureCloneProp &
  NavItemGoNavProps;

type NavItemMenuProps = {
  label: string;
  link?: never;
  onClick?: never;
  active?: boolean;
  menuItems: MenuItem[];
  menuYOffset?: number;
  iconName?: MaterialNames;
  hideIcon?: boolean;
  orientation?: "vertical" | "horizontal";
} & NavItemMeasureCloneProp &
  NavItemGoNavProps;

export type NavItemProps = NavItemLinkProps | NavItemMenuProps | NavItemClickProps;

function isMenuNavItem(props: NavItemProps): props is NavItemMenuProps {
  return "menuItems" in props && Array.isArray(props.menuItems);
}

const randomNum = () => (Math.random() * 10000000).toFixed(0);

export default function NavItem(props: NavItemProps) {
  const { label, active = false, measureClone = false, goNav } = props;
  const iconName: MaterialNames = "iconName" in props ? props.iconName || "person" : "person";
  const menuYOffset = "menuYOffset" in props ? (props.menuYOffset ?? 8) : 8;
  const hideIcon = "hideIcon" in props ? (props.hideIcon ?? false) : false;
  const orientation = "orientation" in props ? (props.orientation ?? "vertical") : "vertical";
  const isMenuVariant = isMenuNavItem(props);

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorId = useMemo(() => `navitem-menu-anchor-${randomNum()}`, []);

  function setVisibility() {
    setOpen((prev) => !prev);
  }

  useEffect(() => {
    if (!isMenuVariant) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, isMenuVariant]);

  const onClickNavItemElement = () => {
    if ("onClick" in props) return props.onClick;
    if (isMenuVariant) return setVisibility;
    return undefined;
  };

  const inGoNavChordMode = Boolean(goNav?.modeActive);
  const showChordHint = Boolean(goNav?.showChordKeyCaps && goNav.chordKey);

  const navItemChromeClassName = classNames(
    "flex items-center bg-transparent px-4 py-3 cursor-pointer",
    "hover:bg-sys-brand-surface-container-high rounded-md border-2",
    showChordHint && "relative",
    inGoNavChordMode ? "border-transparent transition-none" : "transition-all duration-200",
    {
      "text-sys-brand-primary border-sys-brand-primary": active && !inGoNavChordMode,
      "text-sys-brand-on-surface border-transparent": !active || inGoNavChordMode
    }
  );

  const goNavFocusProps = goNav
    ? {
        "data-go-nav-item": true,
        "data-route-active": active ? "true" : undefined,
        tabIndex: -1,
        "data-testid": goNav.dataTestId,
        ...(showChordHint && goNav.chordKey ? { "aria-keyshortcuts": goNav.chordKey.toLowerCase() } : {})
      }
    : {};

  const displayLabel = measureClone ? null : label;

  const navItemBody = (
    <>
      <div
        className={classNames("flex", {
          "flex-col items-center": orientation === "vertical",
          "flex-row items-center space-x-3": orientation === "horizontal"
        })}
      >
        {!hideIcon && (
          <Icon
            name={iconName}
            className={classNames("text-base", {
              "mb-1": orientation === "vertical",
              "text-sys-brand-primary": active && !inGoNavChordMode,
              "text-sys-brand-on-surface": !active || inGoNavChordMode
            })}
          />
        )}

        <span
          className={classNames(
            "text-title-medium",
            {
              "text-center": orientation === "vertical",
              "text-left": orientation === "horizontal"
            },
            "text-center",
            "text-title-medium-bold font-serif"
          )}
          {...(measureClone ? { "data-nav-item-label": label } : {})}
        >
          {displayLabel}
        </span>
      </div>

      {isMenuVariant && (
        <Icon
          name="expand_more"
          className={classNames("ml-2 text-base transition-transform duration-200", {
            "text-sys-brand-primary": active && !inGoNavChordMode,
            "text-sys-brand-on-surface": !active || inGoNavChordMode,
            "rotate-180": open
          })}
        />
      )}
      {showChordHint && goNav?.chordKey ? (
        <KeyboardShortcutHint
          keyLabel={goNav.chordKey}
          showModifier={false}
          size="compact"
          testId="nav-item-chord-hint"
          className="pointer-events-none absolute right-1 top-1"
        />
      ) : null}
    </>
  );

  const navItemElement = isMenuVariant ? (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      className={navItemChromeClassName}
      onClick={setVisibility}
      {...goNavFocusProps}
    >
      {navItemBody}
    </button>
  ) : (
    <div className={navItemChromeClassName} onClick={onClickNavItemElement()} {...goNavFocusProps}>
      {navItemBody}
    </div>
  );

  // Render menu variant with custom positioning
  if (isMenuNavItem(props)) {
    return (
      <div ref={menuRef} className="relative">
        <div id={anchorId}>{navItemElement}</div>
        <LitMenu open={open} anchor={anchorId} yOffset={menuYOffset} menuCorner="start-start" anchorCorner="end-start">
          {props.menuItems.map(
            (
              {
                label,
                iconName,
                onClick,
                disabled,
                itemClassName = "w-[215px]",
                useDivider = true,
                dataTestId = ""
              }: MenuItem,
              index: number
            ) => {
              const onClickHandler = () => {
                onClick?.();
                setOpen(false);
              };
              return (
                <div key={index}>
                  <LitMenuItem
                    className={`${itemClassName}`}
                    onClick={onClickHandler}
                    disabled={disabled}
                    data-testid={dataTestId}
                  >
                    <div className="flex flex-row items-center">
                      {!!iconName && <div className="flex mr-4">{<Icon name={iconName} />}</div>}
                      {label}
                    </div>
                  </LitMenuItem>
                  {index < props.menuItems.length - 1 && useDivider && <Divider />}
                </div>
              );
            }
          )}
        </LitMenu>
      </div>
    );
  }

  if ("link" in props && props.link) {
    return <Link {...props.link}>{navItemElement}</Link>;
  }

  // Render click variant
  return navItemElement;
}
